import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { getStaticRegistryItems, getRouteForItem } from './registry';
import { useRecordSearch } from './useRecordSearch';
import { CommandItem, CommandItemType } from './types';

type CommandBarSection = {
  title: string;
  items: CommandItem[];
  // Renders placeholder rows instead of an absent section while a slow lookup
  // is in flight. Placeholder rows are never selectable, so a loading section
  // contributes nothing to the keyboard index.
  isLoading?: boolean;
};

// The resting bar led with a list of pages to jump to, which read as a nav menu
// and hid the assistant entirely (FLA-8203). It now opens on three real
// questions so the first thing you see is something you can ask.
function getStarterQuestions(t: TFunction): CommandItem[] {
  return [
    t('aiChat.suggestions.vacationDays'),
    t('aiChat.suggestions.whoOnLeave'),
    t('aiChat.suggestions.expensePolicy'),
  ].map((question, i) => ({
    id: `ai-starter-${i}`,
    type: 'ai' as CommandItemType,
    label: question,
    icon: 'phosphor-star-four' as const,
    keywords: [],
    prompt: question,
  }));
}

type UseCommandBarResult = {
  query: string;
  setQuery: (q: string) => void;
  sections: CommandBarSection[];
  selectedIndex: number;
  // DOM id of the highlighted option, for the input's aria-activedescendant.
  activeItemId: string | undefined;
  totalItems: number;
  isLoading: boolean;
  handleSelect: (item: CommandItem) => void;
  handleKeyDown: (e: React.KeyboardEvent) => void;
};

// Shared with CommandBarResults so the input's aria-activedescendant and the
// option's id cannot drift apart.
export function commandBarOptionId(index: number): string {
  return `command-bar-option-${index}`;
}

function matchesQuery(
  item: { label: string; keywords: string[] },
  terms: string[],
): boolean {
  if (terms.length === 0) return true;
  const haystack = [item.label, ...item.keywords].join(' ').toLowerCase();
  return terms.every((term) => haystack.includes(term));
}

function scoreItem(
  item: { label: string; keywords: string[] },
  terms: string[],
): number {
  const labelLower = item.label.toLowerCase();
  let score = 0;
  for (const term of terms) {
    if (labelLower.startsWith(term)) score += 10;
    else if (labelLower.includes(term)) score += 5;
  }
  return score;
}

export function useCommandBar(
  onClose: () => void,
  onAiSelect: (query: string) => void,
): UseCommandBarResult {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const history = useHistory();
  const registryItems = useRef(getStaticRegistryItems());

  const terms = useMemo(
    () =>
      query
        .toLowerCase()
        .split(/\s+/)
        .filter((t) => t.length > 0),
    [query],
  );

  const { items: recordItems, isLoading } = useRecordSearch(query);

  const filteredStatic = useMemo(() => {
    if (terms.length === 0) return registryItems.current;
    return registryItems.current
      .filter((item) => matchesQuery(item, terms))
      .sort((a, b) => scoreItem(b, terms) - scoreItem(a, terms));
  }, [terms]);

  const sections = useMemo(() => {
    const result: CommandBarSection[] = [];

    // Ask always comes first, typed or not.
    result.push({
      title: t('aiChat.commandBar.askSection'),
      items:
        terms.length > 0
          ? [
              {
                id: 'ai-ask',
                type: 'ai' as CommandItemType,
                label: `Ask Assistant: ${query}`,
                icon: 'phosphor-star-four',
                keywords: [],
              },
            ]
          : getStarterQuestions(t),
    });

    const actions = filteredStatic.filter((i) => i.type === 'action');
    if (actions.length > 0) {
      result.push({
        title: 'Actions',
        items: actions.slice(0, 5),
      });
    }

    const gotos = filteredStatic.filter((i) => i.type === 'goto');
    if (gotos.length > 0) {
      result.push({
        title: t('aiChat.commandBar.gotoSection'),
        items: gotos.slice(0, 8),
      });
    }

    // The People heading appears as soon as the lookup starts rather than when
    // it lands, so a slow backend reads as "coming" instead of "nothing" (I4).
    if (recordItems.length > 0 || isLoading) {
      result.push({
        title: 'People',
        items: recordItems,
        isLoading: recordItems.length === 0,
      });
    }

    return result;
  }, [filteredStatic, recordItems, terms, query, isLoading, t]);

  const totalItems = sections.reduce((acc, s) => acc + s.items.length, 0);

  // Enter must always do something once the employee has typed. Selecting only
  // when a single row matched meant "Clock me in" — which matches the AI row
  // AND the Track my time action — left nothing selected and Enter dead
  // (FLA-8207). The AI row renders first, so the default is "ask the assistant".
  useEffect(() => {
    setSelectedIndex(totalItems > 0 && query.trim().length > 0 ? 0 : -1);
  }, [query, totalItems]);

  const getItemAtIndex = useCallback(
    (index: number): CommandItem | null => {
      let cursor = 0;
      for (const section of sections) {
        if (index < cursor + section.items.length) {
          return section.items[index - cursor];
        }
        cursor += section.items.length;
      }
      return null;
    },
    [sections],
  );

  const handleSelect = useCallback(
    (item: CommandItem) => {
      if (item.type === 'ai') {
        onAiSelect(item.prompt ?? query);
        return;
      }

      if (item.type === 'record' && item.route) {
        history.push(item.route);
        onClose();
        return;
      }

      const registryItem = registryItems.current.find((r) => r.id === item.id);
      if (registryItem) {
        history.push(getRouteForItem(registryItem));
        onClose();
      }
    },
    [history, onClose, onAiSelect, query],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev < totalItems - 1 ? prev + 1 : 0));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : totalItems - 1));
          break;
        case 'Enter': {
          if (selectedIndex < 0) break;
          e.preventDefault();
          const item = getItemAtIndex(selectedIndex);
          if (item) handleSelect(item);
          break;
        }
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    },
    [totalItems, selectedIndex, getItemAtIndex, handleSelect, onClose],
  );

  return {
    query,
    setQuery,
    sections,
    selectedIndex,
    activeItemId:
      selectedIndex >= 0 ? commandBarOptionId(selectedIndex) : undefined,
    totalItems,
    isLoading,
    handleSelect,
    handleKeyDown,
  };
}
